// client/src/hooks/useClientes.js - VERSÃO 100% CORRIGIDA (MANTENDO observacoesCliente)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import useAlert from './useAlert';
import { useMe } from './useAuth';

const CLIENTES_QUERY_KEY = 'clientes';

// ✅ FUNÇÃO AUXILIAR: Processar cliente (mantendo compatibilidade)
const processarCliente = (cliente) => {
    if (!cliente) return null;
    
    try {
        const clienteObj = cliente.toObject ? cliente.toObject() : { ...cliente };
        
        // Se cliente.user é string (ID)
        if (typeof clienteObj.user === 'string') {
            return {
                ...clienteObj,
                user: {
                    _id: clienteObj.user,
                    name: 'Carregando...',
                    email: '',
                    telefone: '',
                    photo: null,
                    isActive: true
                }
            };
        }

        // Se temos objeto user
        if (clienteObj.user && typeof clienteObj.user === 'object') {
            const userData = clienteObj.user;
            const processedUser = {
                _id: userData._id || clienteObj._id,
                name: userData.name || 'Cliente sem nome',
                email: userData.email || '',
                telefone: userData.telefone || '',
                cpf: userData.cpf || '',
                photo: userData.photo || null,
                photoUrl: userData.photoUrl || null,
                avatarUrl: userData.avatarUrl || null,
                dataNascimento: userData.dataNascimento || null,
                role: userData.role || null,
                isActive: userData.isActive !== undefined ? userData.isActive : true
            };
            
            return {
                ...clienteObj,
                _id: clienteObj._id || clienteObj.id,
                nome: processedUser.name,
                nomeCompleto: processedUser.name,
                descricao: clienteObj.observacoesCliente || '',
                observacoesCliente: clienteObj.observacoesCliente || '',
                email: processedUser.email,
                telefone: processedUser.telefone,
                cpf: processedUser.cpf,
                dataNascimento: processedUser.dataNascimento,
                user: processedUser,
                tags: Array.isArray(clienteObj.tags) ? clienteObj.tags : [],
                ativo: processedUser.isActive,
                status: processedUser.isActive ? 'ativo' : 'inativo'
            };
        }

        // Fallback
        return {
            ...clienteObj,
            _id: clienteObj._id || clienteObj.id,
            nome: clienteObj.nomeCompleto || clienteObj.nome || 'Cliente',
            nomeCompleto: clienteObj.nomeCompleto || clienteObj.nome || 'Cliente',
            descricao: clienteObj.observacoesCliente || '',
            observacoesCliente: clienteObj.observacoesCliente || '',
            tags: Array.isArray(clienteObj.tags) ? clienteObj.tags : [],
            ativo: clienteObj.status !== 'inativo',
            user: {
                _id: clienteObj._id || clienteObj.id,
                name: clienteObj.nomeCompleto || clienteObj.nome || 'Cliente',
                email: clienteObj.emailPrincipal || clienteObj.email || '',
                telefone: clienteObj.telefonePrincipal || clienteObj.telefone || '',
                photo: null,
                isActive: clienteObj.status !== 'inativo'
            }
        };
    } catch (error) {
        console.error('❌ [processarCliente] Erro:', error);
        return cliente;
    }
};

// ✅ CORREÇÃO: Normalizar filtros para backend
const normalizarFiltrosParaBackend = (filtros = {}) => {
    const resultado = {};
    
    if (!filtros || typeof filtros !== 'object') {
        return resultado;
    }
    
    Object.entries(filtros).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
            return;
        }
        
        const campoBackend = {
            nome: 'name',
            nomeCompleto: 'name',
            telefone: 'telefone',
            telefonePrincipal: 'telefone',
            email: 'email',
            emailPrincipal: 'email',
            cpf: 'cpf',
            status: 'status',
            tags: 'tags',
            dataInicio: 'dataInicio',
            dataFim: 'dataFim',
            sort: 'sort',
            q: 'q',
            page: 'page',
            limit: 'limit',
            account: 'account'
        }[key] || key;
        
        if (Array.isArray(value)) {
            if (value.length > 0) {
                const tagsLimpa = value
                    .filter(tag => tag && typeof tag === 'string' && tag.trim() !== '')
                    .map(tag => tag.trim());
                if (tagsLimpa.length > 0) {
                    resultado[campoBackend] = tagsLimpa.join(',');
                }
            }
            return;
        }
        
        if (value instanceof Date) {
            resultado[campoBackend] = value.toISOString().split('T')[0];
            return;
        }
        
        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (trimmed !== '') {
                if (campoBackend === 'telefone' || campoBackend === 'cpf') {
                    const apenasNumeros = trimmed.replace(/\D/g, '');
                    if (apenasNumeros) {
                        resultado[campoBackend] = apenasNumeros;
                    }
                } else {
                    resultado[campoBackend] = trimmed;
                }
            }
            return;
        }
        
        resultado[campoBackend] = value;
    });
    
    return resultado;
};

// ✅ Construir parâmetros otimizados
const construirParamsCliente = (params = {}, accountId = null) => {
    const defaultParams = {
        page: 1,
        limit: 20,
        sort: '-createdAt',
        populate: 'user,vinculosAccount'
    };
    
    const queryParams = { ...defaultParams, ...params };
    
    if (accountId && accountId !== 'undefined' && accountId !== 'null') {
        queryParams.account = accountId;
    }
    
    const paramsNormalizados = normalizarFiltrosParaBackend(queryParams);
    
    paramsNormalizados.page = parseInt(paramsNormalizados.page, 10) || 1;
    paramsNormalizados.limit = parseInt(paramsNormalizados.limit, 10) || 20;
    
    return paramsNormalizados;
};

// ✅ HOOK PRINCIPAL: useClientes
export const useClientes = (params = {}, accountId = null) => {
    const { user, isLoading: userLoading } = useMe();

    return useQuery({
        queryKey: [CLIENTES_QUERY_KEY, params, accountId],
        queryFn: async () => {
            console.log('='.repeat(60));
            console.log('🔍 [useClientes] INICIANDO BUSCA');
            console.log('📋 Parâmetros recebidos:', params);
            console.log('📋 Account ID:', accountId);
            
            if (!accountId || accountId === 'undefined' || accountId === 'null') {
                console.warn('⚠️ [useClientes] Account ID inválido ou não fornecido');
                return { 
                    success: false,
                    data: [], 
                    count: 0, 
                    totalCount: 0, 
                    pagination: {},
                    message: 'ID da conta inválido'
                };
            }
            
            try {
                const finalParams = construirParamsCliente(params, accountId);
                
                console.log('🚀 [useClientes] FAZENDO REQUISIÇÃO:');
                console.log('   URL: /clientes');
                console.log('   Params:', finalParams);
                
                const response = await apiClient.get('/clientes', { 
                    params: finalParams,
                    headers: { 
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache'
                    },
                    timeout: 30000
                });
                
                console.log('✅ [useClientes] RESPOSTA:');
                console.log('   Status:', response.status);
                console.log('   Quantidade:', response.data?.data?.length || 0);
                
                if (response.data && response.data.success !== false) {
                    const clientesArray = Array.isArray(response.data.data) ? 
                        response.data.data : 
                        (response.data.data ? [response.data.data] : []);
                    
                    const processedData = clientesArray
                        .map(processarCliente)
                        .filter(Boolean);
                    
                    console.log(`✅ [useClientes] ${processedData.length} clientes processados`);
                    
                    return {
                        success: true,
                        data: processedData,
                        count: processedData.length,
                        totalCount: response.data.totalCount || response.data.count || processedData.length,
                        pagination: response.data.pagination || {
                            page: finalParams.page || 1,
                            limit: finalParams.limit || 20,
                            totalPages: Math.ceil((response.data.totalCount || processedData.length) / (finalParams.limit || 20))
                        }
                    };
                }
                
                console.log('ℹ️ [useClientes] Nenhum cliente encontrado ou resposta inválida');
                return { 
                    success: false,
                    data: [], 
                    count: 0, 
                    totalCount: 0,
                    pagination: {
                        page: finalParams.page || 1,
                        limit: finalParams.limit || 20,
                        totalPages: 0
                    },
                    message: response.data?.message || 'Nenhum cliente encontrado'
                };
                
            } catch (error) {
                console.error('❌ [useClientes] ERRO DETALHADO:');
                console.error('   Código:', error.code);
                console.error('   Mensagem:', error.message);
                console.error('   Status:', error.response?.status);
                console.error('   Data:', error.response?.data);
                
                return { 
                    success: false,
                    data: [], 
                    count: 0, 
                    totalCount: 0, 
                    pagination: {},
                    error: {
                        message: error.response?.data?.error || error.message || 'Erro ao buscar clientes',
                        status: error.response?.status,
                        code: error.code,
                        details: error.response?.data
                    }
                };
            } finally {
                console.log('='.repeat(60));
            }
        },
        enabled: !userLoading && !!user && !!accountId && accountId !== 'undefined' && accountId !== 'null',
        staleTime: 1000 * 60,
        gcTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        retry: 2,
        retryDelay: 1000
    });
};

// ✅ HOOK PARA BUSCAR CLIENTE POR ID
export const useClienteById = (id) => {
    return useQuery({
        queryKey: [CLIENTES_QUERY_KEY, id],
        queryFn: async () => {
            console.log('🔍 [useClienteById] Buscando cliente:', id);
            
            if (!id || id === 'undefined' || id === 'null') {
                return null;
            }

            try {
                const { data } = await apiClient.get(`/clientes/${id}`, {
                    params: { 
                        populate: 'user,vinculosAccount,galeria' 
                    }
                });
                
                if (data && data.success && data.data) {
                    const processed = processarCliente(data.data);
                    console.log('✅ [useClienteById] Cliente carregado:', processed._id);
                    return processed;
                }
                return null;
                
            } catch (error) {
                console.error('❌ [useClienteById] Erro:', {
                    message: error.message,
                    status: error.response?.status,
                    data: error.response?.data
                });
                
                if (error.response?.status === 404) {
                    return null;
                }
                
                throw error;
            }
        },
        enabled: !!id && id !== 'undefined' && id !== 'null',
        staleTime: 1000 * 60 * 5,
        retry: 1
    });
};

// ✅ HOOK PARA HISTÓRICO DE AGENDAMENTOS
export const useHistoricoAgendamentos = (clienteId, params = {}) => {
    return useQuery({
        queryKey: ['historicoAgendamentos', clienteId, params],
        queryFn: async () => {
            console.log('📅 [useHistoricoAgendamentos] Buscando histórico para cliente:', clienteId);
            
            if (!clienteId || clienteId === 'undefined' || clienteId === 'null') {
                return { success: false, data: [], count: 0, totalCount: 0, message: 'ID do cliente inválido' };
            }
            
            try {
                const { data } = await apiClient.get(`/clientes/${clienteId}/agendamentos`, { 
                    params: {
                        page: params.page || 1,
                        limit: params.limit || 10,
                        ...params
                    }
                });
                
                return data || { success: false, data: [], count: 0, totalCount: 0 };
            } catch (error) {
                console.error('❌ [useHistoricoAgendamentos] Erro:', error.message);
                return { 
                    success: false, 
                    data: [], 
                    count: 0, 
                    totalCount: 0,
                    error: error.message 
                };
            }
        },
        enabled: !!clienteId && clienteId !== 'undefined' && clienteId !== 'null',
        staleTime: 1000 * 30,
    });
};

// ✅ HOOK PARA RESUMO DE AGENDAMENTOS
export const useResumoAgendamentos = (clienteId) => {
    return useQuery({
        queryKey: ['resumoAgendamentos', clienteId],
        queryFn: async () => {
            console.log('📊 [useResumoAgendamentos] Buscando resumo para cliente:', clienteId);
            
            if (!clienteId || clienteId === 'undefined' || clienteId === 'null') {
                return null;
            }
            
            try {
                const { data } = await apiClient.get(`/clientes/${clienteId}/agendamentos/resumo`);
                return data?.data || {};
            } catch (error) {
                console.error('❌ [useResumoAgendamentos] Erro:', error.message);
                return {};
            }
        },
        enabled: !!clienteId && clienteId !== 'undefined' && clienteId !== 'null',
        staleTime: 1000 * 60,
    });
};

// ✅ FUNÇÃO PARA LIMPAR DADOS DO CLIENTE
// Reduz o payload ao contrato canônico documentado em
// server/controllers/clientesController.js (normalizarCamposCliente):
// { nome, telefone, email, cpf, dataNascimento, account, unidades,
//   enderecoPessoal, tags, observacoesCliente }. Aceita os aliases legados
// (nomeCompleto/telefonePrincipal/emailPrincipal) só para não quebrar chamadores
// antigos — sempre reescritos para o nome canônico antes do envio.
const limparDadosCliente = (clienteData) => {
    if (!clienteData || typeof clienteData !== 'object') {
        return {};
    }
    
    const cleaned = { ...clienteData };
    
    Object.keys(cleaned).forEach(key => {
        const value = cleaned[key];
        if (value === null || value === undefined || value === '') {
            delete cleaned[key];
        }
    });
    
    if (cleaned.nome !== undefined) {
        cleaned.nome = String(cleaned.nome).trim();
        if (cleaned.nome === '') {
            delete cleaned.nome;
        }
    } else if (cleaned.nomeCompleto !== undefined) {
        cleaned.nome = String(cleaned.nomeCompleto).trim();
        if (cleaned.nome === '') {
            delete cleaned.nome;
        }
        delete cleaned.nomeCompleto;
    }
    
    if (cleaned.email !== undefined) {
        if (cleaned.email === '' || cleaned.email === null || cleaned.email === 'null') {
            delete cleaned.email;
        } else if (typeof cleaned.email === 'string') {
            const email = cleaned.email.trim();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (email && emailRegex.test(email)) {
                cleaned.email = email.toLowerCase();
            } else {
                delete cleaned.email;
            }
        }
    } else if (cleaned.emailPrincipal !== undefined) {
        if (cleaned.emailPrincipal === '' || cleaned.emailPrincipal === null || cleaned.emailPrincipal === 'null') {
            delete cleaned.emailPrincipal;
        } else if (typeof cleaned.emailPrincipal === 'string') {
            const email = cleaned.emailPrincipal.trim();
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (email && emailRegex.test(email)) {
                cleaned.email = email.toLowerCase();
            } else {
                delete cleaned.email;
            }
        }
        delete cleaned.emailPrincipal;
    }
    
    if (cleaned.telefone !== undefined && cleaned.telefone) {
        const telefone = String(cleaned.telefone).replace(/\D/g, '');
        if (telefone.length >= 10) {
            cleaned.telefone = telefone;
        } else {
            delete cleaned.telefone;
        }
    } else if (cleaned.telefonePrincipal !== undefined && cleaned.telefonePrincipal) {
        const telefone = String(cleaned.telefonePrincipal).replace(/\D/g, '');
        if (telefone.length >= 10) {
            cleaned.telefone = telefone;
        } else {
            delete cleaned.telefone;
        }
        delete cleaned.telefonePrincipal;
    }
    
    if (cleaned.cpf !== undefined && cleaned.cpf) {
        const cpf = String(cleaned.cpf).replace(/\D/g, '');
        if (cpf.length === 11) {
            cleaned.cpf = cpf;
        } else {
            delete cleaned.cpf;
        }
    }
    
    if (cleaned.dataNascimento !== undefined) {
        if (!cleaned.dataNascimento || cleaned.dataNascimento === '' || cleaned.dataNascimento === 'null') {
            delete cleaned.dataNascimento;
        }
    }
    
    if (cleaned.enderecoPessoal && typeof cleaned.enderecoPessoal === 'object') {
        const enderecoLimpo = {};
        Object.entries(cleaned.enderecoPessoal).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                enderecoLimpo[key] = String(value).trim();
            }
        });
        
        if (Object.keys(enderecoLimpo).length > 0) {
            cleaned.enderecoPessoal = enderecoLimpo;
        } else {
            delete cleaned.enderecoPessoal;
        }
    } else if (cleaned.endereco) {
        const enderecoLimpo = {};
        Object.entries(cleaned.endereco).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                enderecoLimpo[key] = String(value).trim();
            }
        });
        
        if (Object.keys(enderecoLimpo).length > 0) {
            cleaned.enderecoPessoal = enderecoLimpo;
        }
        delete cleaned.endereco;
    }
    
    if (cleaned.tags !== undefined) {
        if (Array.isArray(cleaned.tags)) {
            const tagsFiltradas = cleaned.tags
                .filter(tag => tag && typeof tag === 'string' && tag.trim() !== '')
                .map(tag => tag.trim())
                .filter((tag, index, self) => self.indexOf(tag) === index);
            
            if (tagsFiltradas.length > 0) {
                cleaned.tags = tagsFiltradas;
            } else {
                delete cleaned.tags;
            }
        } else {
            delete cleaned.tags;
        }
    }
    
    if (cleaned.observacoesCliente !== undefined) {
        if (cleaned.observacoesCliente && typeof cleaned.observacoesCliente === 'string') {
            cleaned.observacoesCliente = cleaned.observacoesCliente.trim();
            if (cleaned.observacoesCliente === '') {
                delete cleaned.observacoesCliente;
            }
        } else {
            delete cleaned.observacoesCliente;
        }
    }
    
    if (cleaned.descricao !== undefined && !cleaned.observacoesCliente) {
        if (cleaned.descricao && typeof cleaned.descricao === 'string') {
            cleaned.observacoesCliente = cleaned.descricao.trim();
            if (cleaned.observacoesCliente === '') {
                delete cleaned.observacoesCliente;
            }
        }
        delete cleaned.descricao;
    }
    
    if (cleaned.status !== undefined) {
        // Nota: createCliente/updateCliente ignoram `ativo` — a (in)ativação do
        // cliente só acontece via DELETE /clientes/:id. Mantido aqui só para não
        // quebrar chamadores que ainda enviam `status`.
        cleaned.ativo = cleaned.status === 'ativo';
        delete cleaned.status;
    }
    
    if (cleaned._id === 'new' || cleaned.id === 'new') {
        delete cleaned._id;
        delete cleaned.id;
    }
    
    console.log('🧹 [limparDadosCliente] Dados limpos:', cleaned);
    return cleaned;
};

// ✅ HOOK PARA MUTAÇÕES
export const useMutateClientes = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();
    const { accountId } = useMe();

    const invalidateClientesCache = (id = null) => {
        console.log('🔄 [useMutateClientes] Invalidando cache');
        queryClient.invalidateQueries({ queryKey: [CLIENTES_QUERY_KEY] });
        
        if (id) {
            queryClient.invalidateQueries({ queryKey: [CLIENTES_QUERY_KEY, id] });
            queryClient.invalidateQueries({ queryKey: ['cliente', id] });
        }
    };

    const createCliente = useMutation({
        mutationFn: async (clienteData) => {
            console.log('➕ [createCliente] Criando novo cliente');
            
            const finalData = limparDadosCliente(clienteData);
            
            if (!finalData.account && accountId) {
                finalData.account = accountId;
            }
            
            console.log('📤 [createCliente] Dados para envio:', finalData);
            
            try {
                const response = await apiClient.post('/clientes', finalData);
                console.log('✅ [createCliente] Cliente criado:', response.data);
                return response.data;
            } catch (error) {
                console.error('❌ [createCliente] Erro detalhado:', {
                    status: error.response?.status,
                    data: error.response?.data,
                    message: error.message
                });
                
                let errorMessage = 'Erro ao criar cliente';
                
                if (error.response?.data?.error) {
                    const errorText = error.response.data.error;
                    
                    if (errorText.includes('Email já cadastrado') || errorText.includes('email já está cadastrado')) {
                        errorMessage = 'Email já cadastrado no sistema';
                    } else if (errorText.includes('Telefone já cadastrado') || errorText.includes('telefone já está cadastrado')) {
                        errorMessage = 'Telefone já cadastrado no sistema';
                    } else if (errorText.includes('CPF já cadastrado')) {
                        errorMessage = 'CPF já cadastrado no sistema';
                    } else {
                        errorMessage = errorText;
                    }
                }
                
                throw new Error(errorMessage);
            }
        },
        onSuccess: (response) => {
            console.log('✅ [createCliente] Sucesso - atualizando cache');
            invalidateClientesCache();
            setAlert('Cliente criado com sucesso!', 'success');
            
            if (response.data) {
                const novoCliente = processarCliente(response.data);
                
                queryClient.setQueryData([CLIENTES_QUERY_KEY], (oldData) => {
                    if (!oldData || !oldData.data) return oldData;
                    
                    return {
                        ...oldData,
                        data: [novoCliente, ...oldData.data],
                        count: (oldData.count || 0) + 1,
                        totalCount: (oldData.totalCount || 0) + 1
                    };
                });
            }
        },
        onError: (error) => {
            console.error('❌ [createCliente] Erro no onError:', error.message);
            setAlert(error.message || 'Erro ao criar cliente', 'danger');
        },
    });

    const updateCliente = useMutation({
        mutationFn: async ({ id, clienteData }) => {
            console.log('✏️ [updateCliente] Atualizando cliente:', id);
            
            if (!id || id === 'undefined' || id === 'null') {
                throw new Error('ID do cliente inválido');
            }
            
            const finalData = limparDadosCliente(clienteData);
            
            console.log('📤 [updateCliente] Dados para envio:', {
                id,
                data: finalData
            });
            
            try {
                const response = await apiClient.put(`/clientes/${id}`, finalData);
                console.log('✅ [updateCliente] Resposta:', response.data);
                return response.data;
            } catch (error) {
                console.error('❌ [updateCliente] Erro detalhado:', {
                    status: error.response?.status,
                    data: error.response?.data,
                    message: error.message,
                    url: error.config?.url,
                    method: error.config?.method
                });
                
                let errorMessage = 'Erro ao atualizar cliente';
                
                if (error.response?.data?.error) {
                    const errorText = error.response.data.error;
                    
                    if (errorText.includes('id is not defined')) {
                        errorMessage = 'Erro no servidor: ID não definido (problema no backend)';
                    } else if (errorText.includes('Email já cadastrado')) {
                        errorMessage = 'Email já cadastrado no sistema';
                    } else if (errorText.includes('Telefone já cadastrado')) {
                        errorMessage = 'Telefone já cadastrado no sistema';
                    } else if (errorText.includes('Não autorizado')) {
                        errorMessage = 'Você não tem permissão para atualizar este cliente';
                    } else if (error.response.status === 404) {
                        errorMessage = 'Cliente não encontrado';
                    } else {
                        errorMessage = errorText;
                    }
                }
                
                throw new Error(errorMessage);
            }
        },
        onSuccess: (response, variables) => {
            console.log('✅ [updateCliente] Cliente atualizado:', variables.id);
            
            if (response.data) {
                const updatedCliente = processarCliente(response.data);
                
                invalidateClientesCache(variables.id);
                setAlert('Cliente atualizado com sucesso!', 'success');
                
                queryClient.setQueryData([CLIENTES_QUERY_KEY], (oldData) => {
                    if (!oldData || !oldData.data) return oldData;
                    
                    return {
                        ...oldData,
                        data: oldData.data.map(cliente => 
                            cliente._id === variables.id ? updatedCliente : cliente
                        )
                    };
                });
                
                queryClient.setQueryData([CLIENTES_QUERY_KEY, variables.id], updatedCliente);
            }
        },
        onError: (error) => {
            console.error('❌ [updateCliente] Erro no onError:', error.message);
            setAlert(error.message || 'Erro ao atualizar cliente', 'danger');
        },
    });

    const deleteCliente = useMutation({
        mutationFn: async (id) => {
            console.log('🗑️ [deleteCliente] Removendo cliente:', id);
            
            if (!id || id === 'undefined' || id === 'null') {
                throw new Error('ID do cliente inválido');
            }
            
            return apiClient.delete(`/clientes/${id}`);
        },
        onSuccess: (response, clienteId) => {
            console.log('✅ [deleteCliente] Cliente removido:', clienteId);
            invalidateClientesCache(clienteId);
            setAlert('Cliente removido com sucesso!', 'success');
            
            queryClient.setQueryData([CLIENTES_QUERY_KEY], (oldData) => {
                if (!oldData || !oldData.data) return oldData;
                
                return {
                    ...oldData,
                    data: oldData.data.filter(cliente => cliente._id !== clienteId),
                    count: Math.max(0, (oldData.count || 0) - 1),
                    totalCount: Math.max(0, (oldData.totalCount || 0) - 1)
                };
            });
        },
        onError: (error) => { 
            console.error('❌ [deleteCliente] Erro:', error.message);
            setAlert(error.message || 'Erro ao remover cliente', 'danger');
        }
    });
    
    const uploadFotoPerfil = useMutation({
        mutationFn: async ({ clienteId, file }) => {
            console.log('📸 [uploadFotoPerfil] Enviando foto:', clienteId);
            
            if (!clienteId || !file) {
                throw new Error('ID do cliente ou arquivo inválido');
            }
            
            const formData = new FormData();
            formData.append('file', file);
            
            return apiClient.put(`/clientes/${clienteId}/foto`, formData, { 
                headers: { 'Content-Type': 'multipart/form-data' } 
            });
        },
        onSuccess: (data, variables) => {
            console.log('✅ [uploadFotoPerfil] Foto enviada');
            invalidateClientesCache(variables.clienteId);
            setAlert('Foto de perfil atualizada!', 'success');
        },
        onError: (error) => {
            console.error('❌ [uploadFotoPerfil] Erro:', error.message);
            setAlert(error.message || 'Erro ao enviar foto', 'danger');
        },
    });

    const uploadImagemGaleria = useMutation({
        mutationFn: async ({ clienteId, file, categoria = 'Antes' }) => {
            console.log('🖼️ [uploadImagemGaleria] Enviando imagem:', { clienteId, categoria });
            
            if (!clienteId || !file) {
                throw new Error('ID do cliente ou arquivo inválido');
            }
            
            const formData = new FormData();
            formData.append('file', file);
            formData.append('categoria', categoria);
            
            return apiClient.post(`/clientes/${clienteId}/galeria`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        },
        onSuccess: (data, variables) => {
            console.log('✅ [uploadImagemGaleria] Imagem adicionada');
            invalidateClientesCache(variables.clienteId);
            setAlert('Imagem adicionada à galeria!', 'success');
        },
        onError: (error) => {
            console.error('❌ [uploadImagemGaleria] Erro:', error.message);
            setAlert(error.message || 'Erro ao enviar imagem', 'danger');
        },
    });

    const isPending = createCliente.isPending || updateCliente.isPending || deleteCliente.isPending || 
                      uploadFotoPerfil.isPending || uploadImagemGaleria.isPending;

    return { 
        createCliente, 
        updateCliente, 
        deleteCliente, 
        uploadFotoPerfil, 
        uploadImagemGaleria,
        isPending
    };
};

// ✅ HOOKS ADICIONAIS

export const useTodosClientes = (params = {}) => {
    const { user, isLoading: userLoading } = useMe();
    
    return useQuery({
        queryKey: ['todos-clientes', params],
        queryFn: async () => {
            console.log('🔍 [useTodosClientes] Buscando TODOS os clientes');
            
            try {
                const response = await apiClient.get('/clientes', {
                    params: {
                        page: params.page || 1,
                        limit: params.limit || 50,
                        populate: 'user',
                        ...params
                    }
                });
                
                console.log('✅ [useTodosClientes] Resposta:', {
                    success: response.data?.success,
                    count: response.data?.data?.length || 0
                });
                
                if (response.data && response.data.success && response.data.data) {
                    const processed = {
                        ...response.data,
                        data: response.data.data.map(processarCliente)
                    };
                    console.log(`✅ [useTodosClientes] ${processed.data.length} clientes encontrados`);
                    return processed;
                }
                return { success: false, data: [], count: 0, totalCount: 0 };
            } catch (error) {
                console.error('❌ [useTodosClientes] Erro:', error.message);
                return { success: false, data: [], count: 0, totalCount: 0, error: error.message };
            }
        },
        enabled: !userLoading && !!user,
        staleTime: 1000 * 60
    });
};

export const useClienteGaleria = (clienteId) => {
    return useQuery({
        queryKey: ['clienteGaleria', clienteId],
        queryFn: async () => {
            console.log('🖼️ [useClienteGaleria] Buscando galeria:', clienteId);
            
            if (!clienteId || clienteId === 'undefined' || clienteId === 'null') {
                return [];
            }
            
            try {
                const { data } = await apiClient.get(`/clientes/${clienteId}/galeria`);
                
                if (data && data.success && data.data) {
                    const galeria = data.data || [];
                    console.log(`✅ [useClienteGaleria] ${galeria.length} imagens na galeria`);
                    return galeria;
                }
                return [];
                
            } catch (error) {
                console.error('❌ [useClienteGaleria] Erro:', error.message);
                return [];
            }
        },
        enabled: !!clienteId && clienteId !== 'undefined' && clienteId !== 'null',
        staleTime: 1000 * 60,
    });
};

export const useEstatisticasClientes = (accountId = null) => {
    return useQuery({
        queryKey: ['estatisticas-clientes', accountId],
        queryFn: async () => {
            console.log('📊 [useEstatisticasClientes] Buscando estatísticas');
            
            const params = accountId ? { accountId } : {};
            
            try {
                const { data } = await apiClient.get('/clientes/estatisticas', { params });
                return data?.data || {};
            } catch (error) {
                console.error('❌ [useEstatisticasClientes] Erro:', error.message);
                return {};
            }
        },
        staleTime: 1000 * 60 * 5,
    });
};

export const useTestarBackendCliente = () => {
    const { accountId } = useMe();
    
    return useQuery({
        queryKey: ['test-backend-cliente', accountId],
        queryFn: async () => {
            console.log('🧪 [useTestarBackendCliente] Testando endpoints');
            
            const testes = [];
            
            try {
                const res1 = await apiClient.get('/clientes', { 
                    params: { limit: 3, populate: 'user' } 
                });
                testes.push({ 
                    nome: 'Todos clientes', 
                    status: '✅', 
                    count: res1.data?.data?.length || 0,
                    success: res1.data?.success 
                });
            } catch (err) {
                testes.push({ 
                    nome: 'Todos clientes', 
                    status: '❌', 
                    error: err.message,
                    statusCode: err.response?.status
                });
            }
            
            try {
                const res2 = await apiClient.get('/clientes/69721b26aa8bfbb5f8765210');
                testes.push({ 
                    nome: 'Cliente específico', 
                    status: '✅', 
                    id: res2.data?.data?._id,
                    success: res2.data?.success 
                });
            } catch (err) {
                testes.push({ 
                    nome: 'Cliente específico', 
                    status: '❌', 
                    error: err.message,
                    statusCode: err.response?.status
                });
            }
            
            console.log('🧪 [useTestarBackendCliente] Resultados:', testes);
            return testes;
        },
        enabled: true,
        retry: false
    });
};

export default {
    useClientes,
    useClienteById,
    useHistoricoAgendamentos,
    useResumoAgendamentos,
    useMutateClientes,
    useTodosClientes,
    useClienteGaleria,
    useEstatisticasClientes,
    useTestarBackendCliente
};